import { useRef } from 'react';
import type { Editor } from '@tiptap/react';
import Tooltip from '../EditorTopBar/Tooltip';
import { insertRefImageFiles, selectedVisualCell } from './RefImage';

/**
 * The dock's Image button (2B, mock-up 06 §01). Opens the file picker; each
 * chosen file lands, in order, in the Visual cell holding the cursor. Dimmed,
 * never removed, outside a Visual cell, with the reason on hover and focus,
 * as the top bar's rail does.
 */
export default function RefImageButton({ editor, iconOnly = false }: { editor: Editor; iconOnly?: boolean }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const cellPos = selectedVisualCell(editor.state);
  const enabled = cellPos !== null && editor.isEditable;
  const tip = enabled
    ? 'Reference image · or paste, or drop a file on the cell'
    : 'Image · click into a Visual cell first';

  return (
    <>
      <Tooltip content={tip}>
        <button
          type="button"
          aria-disabled={!enabled || undefined}
          aria-label={tip}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            if (enabled) inputRef.current?.click();
          }}
          className={`
            ref-image-button inline-flex items-center justify-center gap-1.5 px-2.5 py-1 rounded-sm
            font-mono text-[11px] uppercase tracking-[0.08em] font-medium
            transition-colors duration-150
            ${enabled ? 'text-muted hover:text-fg hover:bg-elevated' : 'opacity-30 cursor-not-allowed text-muted'}
          `}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <circle cx="9" cy="10" r="2" />
            <path d="m21 16-5-5-9 9" />
          </svg>
          {!iconOnly && 'Image'}
        </button>
      </Tooltip>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        hidden
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          event.target.value = '';
          // The cursor may have moved while the picker was open; use the cell
          // it is in now, which the insert re-checks.
          if (files.length > 0) insertRefImageFiles(editor.view, files);
        }}
      />
    </>
  );
}
